# Glucose-Friendly Dietary Guidance

**CORE PRINCIPLE:** Focus on foods with glycemic index below 55. Replace high-glycemic carbohydrates with low-glycemic alternatives while maintaining the dish's essential character and satisfaction.

## INGREDIENT REPLACEMENT STRATEGIES

### For High-Glycemic Grains & Starches
**PRINCIPLE:** Replace refined/white carbohydrates with vegetable-based alternatives or small portions of whole grains

**EXAMPLES:** 
- White rice → cauliflower rice, broccoli rice, quinoa (small portions)
- Pasta → zucchini noodles, shirataki noodles, spaghetti squash, kelp noodles
- White bread → lettuce wraps, portobello mushroom caps, almond flour bread
- Regular potatoes → mashed cauliflower, roasted turnips, jicama

**APPROACH:** When encountering ANY refined grain or starchy base, ask: What vegetable can mimic this texture? What whole grain alternative exists in smaller portions?

### For Sugars & Sweeteners
**PRINCIPLE:** Replace all added sugars with natural zero/low-glycemic alternatives

**EXAMPLES:**
- White sugar → stevia, erythritol, monk fruit
- Honey → stevia + small amount of raw honey for flavor
- Brown sugar → erythritol + molasses or stevia + vanilla
- Maple syrup → sugar-free maple syrup or stevia + maple extract

**APPROACH:** Reduce overall sweetness and rely on natural fruit flavors (berries, citrus) when possible

### For High-Glycemic Vegetables
**PRINCIPLE:** Replace starchy vegetables with non-starchy alternatives that provide similar satisfaction

**EXAMPLES:**
- Potatoes → cauliflower, turnips, radishes, celeriac
- Corn → diced bell peppers, jicama, water chestnuts
- Carrots (large amounts) → bell peppers, radishes, celery

**APPROACH:** Consider texture and cooking method - what low-starch vegetable can be prepared similarly?

### For High-Sugar Fruits
**PRINCIPLE:** Replace high-glycemic fruits with low-glycemic alternatives or use in much smaller quantities

**EXAMPLES:**
- Bananas → berries, avocado (for creaminess)
- Dates → fresh berries or small amounts of stevia
- Dried fruits → fresh citrus zest, small amounts of fresh berries
- Pineapple → cucumber with lime, small amounts of berries

**APPROACH:** Prioritize berries and citrus; use high-sugar fruits only as minimal flavor accents

## INTELLIGENT SUBSTITUTION MINDSET

When encountering any ingredient, ask yourself:
1. **"What is this ingredient's role in the dish?"** (sweetness, bulk, texture, flavor base, binding)
2. **"What glucose-friendly ingredient can serve the same role?"**
3. **"How do I adjust cooking method/seasoning to make the substitute delicious?"**

## PRIORITIZE THESE FOOD CATEGORIES

- **Non-starchy vegetables** (unlimited): leafy greens, broccoli, cauliflower, zucchini, bell peppers, asparagus, Brussels sprouts, cabbage, spinach, kale
- **Lean proteins with healthy fats**: fish, chicken, turkey, tofu, tempeh, eggs, grass-fed beef (moderate)
- **Nuts, seeds, and their flours/butters**: almonds, walnuts, chia seeds, flax seeds, hemp hearts, almond flour, coconut flour
- **Low-glycemic fruits**: strawberries, blueberries, raspberries, blackberries, lemons, limes, small amounts of apple
- **Healthy fats**: avocado, olive oil, coconut oil, MCT oil, grass-fed butter (if not dairy-free)
- **High-fiber legumes (in moderation)**: lentils, chickpeas, black beans (smaller portions)

## COOKING TECHNIQUE PRINCIPLES

- **Combine remaining carbs with protein and fiber** to slow glucose absorption
- **Add acid** (vinegar, citrus juice) to help moderate glycemic response
- **Use healthy fats** to increase satiety and slow digestion
- **Focus on vegetable volume** to maintain portion satisfaction
- **Cook vegetables to desired texture** - don't overcook to maintain fiber benefits
- **Use herbs and spices liberally** for flavor without adding sugars

## AVOID ENTIRELY

- All refined sugars and syrups (white sugar, brown sugar, corn syrup, agave)
- White/refined grains and flours (white rice, white bread, white pasta, white flour)
- High-starch vegetables as main components (potatoes, sweet potatoes, corn as primary ingredients)
- Processed foods with hidden sugars (most packaged snacks, sauces, dressings)
- High-glycemic fruits as primary ingredients (watermelon, pineapple, dates, ripe bananas, grapes, mangoes)
- Instant or quick-cooking grains (instant oats, minute rice, quick-cooking barley)

## RECIPE ADAPTATION APPROACH

This principle-based guidance ensures you can intelligently adapt ANY recipe to be glucose-friendly by:
1. Identifying the high-glycemic components
2. Applying the appropriate substitution principles
3. Maintaining the dish's essential character and satisfaction
4. Using cooking techniques that further support blood sugar stability

The goal is not to eliminate enjoyment, but to create satisfying, delicious meals that happen to be glucose-friendly through intelligent ingredient choices and cooking methods.